import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Clock, User, MessageCircle } from "lucide-react";
import { format, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import type { Tables, Json } from "@/integrations/supabase/types";

interface Props {
  sessionId: string;
  onBack: () => void;
}

interface SessionWithRelations extends Tables<"training_sessions"> {
  training_groups: { name: string; color: string | null } | null;
  profiles: { full_name: string | null } | null;
  courts: { name: string } | null;
  coach_note?: string;
}

interface SessionBlockWithExercise extends Tables<"session_blocks"> {
  exercises: { name: string } | null;
}

interface AttendeeWithProfile {
  player_id: string;
  rsvp_status: string;
  profiles: { full_name: string | null } | null;
}

interface FeedbackWithCoach {
  comment: string | null;
  skill_ratings: Json | null;
  profiles: { full_name: string | null } | null;
}

export default function PlayerSessionDetail({ sessionId, onBack }: Props) {
  const { user } = useAuth();

  const { data: session } = useQuery({
    queryKey: ["player-session-detail", sessionId],
    queryFn: async () => {
      const { data } = await supabase
        .from("training_sessions")
        .select(
          "*, training_groups(name, color), profiles!training_sessions_coach_id_fkey(full_name), courts(name)"
        )
        .eq("id", sessionId)
        .single();
      return data as unknown as SessionWithRelations | null;
    },
  });

  const { data: blocks = [] } = useQuery({
    queryKey: ["player-session-blocks", sessionId],
    queryFn: async () => {
      const { data } = await supabase
        .from("session_blocks")
        .select("*, exercises(name)")
        .eq("session_id", sessionId)
        .order("sort_order");
      return (data ?? []) as unknown as SessionBlockWithExercise[];
    },
  });

  // Attending players
  const { data: attendingPlayers = [] } = useQuery({
    queryKey: ["player-session-attendees", sessionId],
    queryFn: async () => {
      const { data } = await supabase
        .from("session_attendance")
        .select("player_id, rsvp_status, profiles!session_attendance_player_id_fkey(full_name)")
        .eq("session_id", sessionId)
        .eq("rsvp_status", "attending");
      return (data ?? []) as unknown as AttendeeWithProfile[];
    },
  });

  // Individual feedback for this player
  const { data: myFeedback } = useQuery({
    queryKey: ["player-session-feedback", sessionId, user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("player_feedback")
        .select("comment, skill_ratings, profiles!player_feedback_coach_id_fkey(full_name)")
        .eq("session_id", sessionId)
        .eq("player_id", user!.id)
        .maybeSingle();
      return data as unknown as FeedbackWithCoach | null;
    },
    enabled: !!user,
  });

  const BLOCK_LABELS: Record<string, string> = {
    warmup: "Uppvärmning",
    drill: "Övning",
    match: "Match",
    cooldown: "Nedvarvning",
    break: "Paus",
    other: "Övrigt",
  };

  if (!session) return null;

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={onBack}>
        <ArrowLeft className="h-4 w-4 mr-1" /> Tillbaka
      </Button>

      <Card>
        <CardContent className="py-4 px-4 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            {session.training_groups?.color && (
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: session.training_groups.color }}
              />
            )}
            <span className="font-semibold">{session.training_groups?.name}</span>
          </div>
          <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
            <span>{format(parseISO(session.session_date), "EEEE d MMMM yyyy", { locale: sv })}</span>
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {session.start_time?.slice(0, 5)} ({session.duration_minutes} min)
            </span>
          </div>
          {session.courts?.name && (
            <p className="text-sm text-muted-foreground">{session.courts.name}</p>
          )}
          {session.profiles?.full_name && (
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <User className="h-3.5 w-3.5" /> {session.profiles.full_name}
            </p>
          )}
          {session.focus_areas && (session.focus_areas as string[]).length > 0 && (
            <div className="flex gap-1 flex-wrap">
              {(session.focus_areas as string[]).map((f) => (
                <Badge key={f} variant="secondary" className="text-xs">
                  {f}
                </Badge>
              ))}
            </div>
          )}
          {session.summary && (
            <p className="text-sm mt-2">{session.summary}</p>
          )}
          {session.coach_note && (
            <div className="mt-2 p-2 bg-muted/50 rounded-md">
              <p className="text-xs font-medium text-muted-foreground mb-0.5">Tränarens notering</p>
              <p className="text-sm italic">"{session.coach_note}"</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Attending players */}
      {attendingPlayers.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Vem är där idag? ({attendingPlayers.length})</h3>
          <Card>
            <CardContent className="py-3 px-4">
              <div className="flex flex-wrap gap-2">
                {attendingPlayers.map((p) => (
                  <Badge key={p.player_id} variant="secondary" className="text-xs">
                    {p.profiles?.full_name || "Okänd"}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {blocks.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Passplanering</h3>
          {blocks.map((b, i: number) => (
            <Card key={b.id}>
              <CardContent className="py-2.5 px-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge variant="outline" className="text-[10px] flex-shrink-0">
                      {BLOCK_LABELS[b.block_type] || b.block_type}
                    </Badge>
                    <span className="text-sm font-medium truncate">
                      {b.title || b.exercises?.name || `Block ${i + 1}`}
                    </span>
                  </div>
                  {b.duration_minutes && (
                    <span className="text-xs text-muted-foreground flex-shrink-0">
                      {b.duration_minutes} min
                    </span>
                  )}
                </div>
                {b.description && (
                  <p className="text-xs text-muted-foreground mt-1">{b.description}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Individual feedback */}
      {myFeedback?.comment && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold flex items-center gap-1">
            <MessageCircle className="h-3.5 w-3.5" /> Personlig feedback
          </h3>
          <Card>
            <CardContent className="py-3 px-4 space-y-1">
              <p className="text-sm italic">"{myFeedback.comment}"</p>
              {myFeedback.profiles?.full_name && (
                <p className="text-xs text-muted-foreground">— {myFeedback.profiles.full_name}</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
